/**
 * F14.3 — OPERACIONES OFFLINE DE MANTENIMIENTO (técnico)
 *
 * Los cuatro tipos del técnico en el registro común (`operaciones.ts`). Viajan en el MISMO lote de `POST /api/sync`
 * que Limpieza: misma cola, mismo orden, misma idempotencia por `idOperacionCliente`, mismas dependencias. Nada de
 * cola, base o endpoint propios. El servidor (`backend/src/mantenimiento/campo.ts`) revalida cada una con el dominio
 * de siempre (`partes.ts`, `consumos.ts`).
 *
 *   MNT_INICIAR    `idEntidad` = parte. Deja el id real de la intervención en `resultadoMnt`.
 *   MNT_NOTA       `idEntidad` = intervención, o el `idOperacionCliente` de su MNT_INICIAR (`dependeDe`).
 *   MNT_CONSUMO    `idEntidad` = parte. Una INTENCIÓN de consumo: el ledger decide al sincronizar.
 *   MNT_FINALIZAR  `idEntidad` = intervención (o el marcador, igual que la nota).
 *
 * Las fotos son `EVIDENCIA_SUBIR` con ámbito `PT` (multipart, transporte propio, `operaciones-limpieza.ts`).
 */
import type { Mutacion, ResultadoMntMutacion } from './db.ts';
import { type DatosAplicada, registrarOperacion, type ResultadoAplicadoLote } from './operaciones.ts';

/** Lo que el servidor devolvió (`MntSync`), guardado en la propia operación junto con SINCRONIZADA. */
function datosMnt(_m: Mutacion, r: ResultadoAplicadoLote): DatosAplicada {
  const x = r['mnt'] as ResultadoMntMutacion | undefined;
  if (x === undefined) return {};
  return {
    resultadoMnt: {
      idPt: x.idPt,
      estadoPt: x.estadoPt,
      idIntervencion: x.idIntervencion,
      idConsumo: x.idConsumo,
      idNota: x.idNota,
      horaEfectiva: x.horaEfectiva,
      avisos: x.avisos,
    },
  };
}

const textoObligatorio = (campo: string, falta: string) => (payload: Mutacion['payload']): string | null => {
  const v = (payload as unknown as Record<string, unknown>)[campo];
  return typeof v === 'string' && v.trim() !== '' ? null : falta;
};

registrarOperacion({ tipo: 'MNT_INICIAR', etiqueta: 'Inicio de intervención', dominio: 'MANTENIMIENTO', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosMnt });
registrarOperacion({
  tipo: 'MNT_NOTA', etiqueta: 'Nota de intervención', dominio: 'MANTENIMIENTO', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosMnt,
  validarLocal: textoObligatorio('texto', 'La nota está vacía.'),
});
registrarOperacion({
  tipo: 'MNT_CONSUMO', etiqueta: 'Consumo de material', dominio: 'MANTENIMIENTO', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosMnt,
  validarLocal: (payload) => {
    const c = (payload as { cantidad?: unknown }).cantidad;
    return typeof c === 'string' && /^\d+(\.\d{1,6})?$/.test(c) && Number(c) > 0 ? null : 'La cantidad no es válida.';
  },
});
registrarOperacion({
  tipo: 'MNT_FINALIZAR', etiqueta: 'Fin de intervención', dominio: 'MANTENIMIENTO', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosMnt,
  validarLocal: textoObligatorio('trabajoRealizado', 'Falta el trabajo realizado.'),
});
